'use client';

/**
 * PageContentProvider — Visual Content Editing.
 *
 * Fetcha /api/page-content al mount ed espone:
 *  - useContent(path, fallback, locale?) → valore dal DB o fallback (testo attuale)
 *  - useContentImage(path, fallback)     → variante non-i18n (bucket `shared`)
 *  - usePageContentStore()               → API interna per l'overlay editor
 *      (applyPatch optimistic + refresh) così l'edit si riflette live.
 *
 * Pattern allineato a DesignVariantsProvider: SSR-safe, sessionStorage cache,
 * fallback ai testi hardcoded se il fetch fallisce o la chiave manca.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useLocale } from '@/components/LocaleProvider';

const SS_KEY = 'page_content_v1';
const SHARED_BUCKET = 'shared';

/** Bucket per locale (it/en/de) + `shared` (valori non-i18n). */
type PageContent = Record<string, Record<string, string>>;

interface PageContentStore {
  content: PageContent;
  /** Applica un set in-memory (optimistic) dopo un salvataggio editor. */
  applyPatch: (path: string, value: string, locale?: string | null) => void;
  /** Sostituisce l'intero contenuto (es. dopo refetch). */
  setContent: (content: PageContent) => void;
  /** Re-fetcha da /api/page-content. */
  refresh: () => Promise<void>;
}

const PageContentContext = createContext<PageContentStore>({
  content: {},
  applyPatch: () => {},
  setContent: () => {},
  refresh: async () => {},
});

function readCache(): PageContent {
  if (typeof window === 'undefined') return {};
  try {
    const cached = sessionStorage.getItem(SS_KEY);
    if (cached) return JSON.parse(cached) as PageContent;
  } catch {
    /* ignore */
  }
  return {};
}

function writeCache(content: PageContent) {
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify(content));
  } catch {
    /* ignore */
  }
}

export default function PageContentProvider({ children }: { children: React.ReactNode }) {
  const [content, setContentState] = useState<PageContent>(() => readCache());

  const setContent = useCallback((c: PageContent) => {
    setContentState(c);
    writeCache(c);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch('/api/page-content', { cache: 'no-store' });
      const j = await r.json();
      if (j?.success && j?.data && typeof j.data === 'object') {
        setContent(j.data as PageContent);
      }
    } catch {
      /* keep current */
    }
  }, [setContent]);

  const applyPatch = useCallback((path: string, value: string, locale?: string | null) => {
    const bucket = locale || SHARED_BUCKET;
    setContentState((prev) => {
      const next: PageContent = { ...prev, [bucket]: { ...(prev[bucket] || {}), [path]: value } };
      writeCache(next);
      return next;
    });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Preview live SENZA salvare (usata da /admin/embed-designer, sezione "Testi per
  // pagina"): il parent frame manda insula-preview-content {patches:[{path,value,locale}]}
  // via postMessage, riusa applyPatch (stesso meccanismo optimistic del live-theme-editor)
  // — override solo in-memory, mai persistito finché l'admin non preme "Salva".
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (!e.data || e.data.type !== 'insula-preview-content' || !Array.isArray(e.data.patches)) return;
      for (const p of e.data.patches) {
        if (p && typeof p.path === 'string' && typeof p.value === 'string') {
          applyPatch(p.path, p.value, typeof p.locale === 'string' ? p.locale : null);
        }
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [applyPatch]);

  const store = useMemo<PageContentStore>(
    () => ({ content, applyPatch, setContent, refresh }),
    [content, applyPatch, setContent, refresh],
  );

  return <PageContentContext.Provider value={store}>{children}</PageContentContext.Provider>;
}

/** API interna per l'overlay editor. */
export function usePageContentStore(): PageContentStore {
  return useContext(PageContentContext);
}

/**
 * useContent — legge un testo i18n dal page_content.
 * @param path     path registry, es. 'hero.title'
 * @param fallback testo hardcoded attuale (usato se il DB non ha la chiave)
 * @param locale   forza un locale; default = locale attivo dell'app
 */
export function useContent(path: string, fallback: string, locale?: string): string {
  const { content } = useContext(PageContentContext);
  const { locale: activeLocale } = useLocale();
  const loc = locale || activeLocale || 'it';
  const fromLocale = content[loc]?.[path];
  if (typeof fromLocale === 'string' && fromLocale.length > 0) return fromLocale;
  // fallback cross-locale: se manca nella lingua attiva ma esiste in IT
  const fromIt = content.it?.[path];
  if (typeof fromIt === 'string' && fromIt.length > 0) return fromIt;
  return fallback;
}

/**
 * useContentImage — legge un valore non-i18n (immagine/URL) dal bucket `shared`.
 */
export function useContentImage(path: string, fallback: string): string {
  const { content } = useContext(PageContentContext);
  const val = content[SHARED_BUCKET]?.[path];
  if (typeof val === 'string' && val.length > 0) return val;
  return fallback;
}
